import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "../../components/primitives/button";
import {
  getMarketingWorkspaceData,
  saveMarketingCampaign,
} from "../../api/reporting";

const DEMOGRAPHIC_OPTIONS = [
  { value: "ALL", label: "All demographics" },
  { value: "18_24", label: "18-24" },
  { value: "25_34", label: "25-34" },
  { value: "35_PLUS", label: "35+" },
];

const ACTIVITY_OPTIONS = [
  { value: "ALL", label: "All users" },
  { value: "INACTIVE_7", label: "Inactive for 7+ days" },
  { value: "INACTIVE_14", label: "Inactive for 14+ days" },
  { value: "INACTIVE_30", label: "Inactive for 30+ days" },
  { value: "HIGH", label: "High (active in last 2 days)" },
  { value: "MEDIUM", label: "Medium (active in last 7 days)" },
  { value: "LOW", label: "Low (active in last 30 days)" },
  { value: "DORMANT", label: "Dormant (>30 days)" },
];

const ONBOARDING_OPTIONS = [
  { value: "ALL", label: "Any stage" },
  { value: "SIGNED_UP", label: "Signed up" },
  { value: "PROFILE_COMPLETE", label: "Profile complete" },
  { value: "FIRST_CHAT", label: "First chat started" },
];

const TIMEZONE_OPTIONS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
];
const MARKETING_SAFE_RATE_PER_SECOND = 7;
const MARKETING_MAX_THROTTLE_PER_MINUTE = MARKETING_SAFE_RATE_PER_SECOND * 60;
const MARKETING_DEFAULT_THROTTLE_PER_MINUTE = 300;
const MARKETING_EMAIL_BODY_START_MARKER = "<!--BI_MARKETING_BODY_START-->";
const MARKETING_EMAIL_BODY_END_MARKER = "<!--BI_MARKETING_BODY_END-->";
const MARKETING_TEST_RECIPIENT_PREFIX = "TEST_RECIPIENT::";

const DEFAULT_REASON = "You are receiving this because you opted in to Better Intentions updates.";

const DEFAULT_FILTERS = {
  region: "ALL",
  demographics: "ALL",
  activityLevel: "ALL",
  onboardingStage: "ALL",
  interests: "ALL",
};
const DEFAULT_COMPOSER = Object.freeze({
  campaignId: "",
  channel: "EMAIL",
  campaignName: "",
  subjectA: "",
  subjectB: "",
  titleA: "",
  titleB: "",
  contentA: "",
  contentB: "",
  ctaLabel: "",
  ctaUrl: "",
  scheduleAt: "",
  throttlePerMinute: MARKETING_DEFAULT_THROTTLE_PER_MINUTE,
  timezone: "America/New_York",
  abEnabled: false,
});
const EMPTY_ARRAY = Object.freeze([]);
const MARKETING_ALL_SECTIONS = Object.freeze([
  "analytics",
  "audience",
  "composer",
  "regional",
  "history",
  "governance",
]);
const MARKETING_FILTER_DEPENDENT_SECTIONS = Object.freeze([
  "analytics",
  "audience",
  "regional",
]);
const MARKETING_CAMPAIGN_SECTIONS = Object.freeze([
  "composer",
  "history",
  "governance",
]);
const US_STATES = Object.freeze([
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DE", "Delaware"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
]);

function formatPct(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function isPlausibleEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function toKFormatter(value) {
  const n = Number(value || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.round(n));
}

function formatCount(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString();
}

function toDateTimeLocalInputValue(isoValue) {
  const ms = Date.parse(String(isoValue || ""));
  if (!Number.isFinite(ms)) return "";
  const date = new Date(ms - new Date(ms).getTimezoneOffset() * 60000);
  return date.toISOString().slice(0, 16);
}

function toIsoOrNullFromLocal(localDateTime) {
  const normalized = String(localDateTime || "").trim();
  if (!normalized) return null;
  const date = new Date(normalized);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toISOString();
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildBrandHeaderHtml() {
  return `
    <div style="margin:0 0 24px;padding:0 4px;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;max-width:320px;margin:0 auto;">
        <tr>
          <td align="center" style="padding-top:0;font-family:'American Typewriter','AmericanTypewriter',Georgia,serif;font-size:28px;line-height:1.15;color:#0f0f12;font-weight:400;letter-spacing:0;">
            Better Intentions
          </td>
        </tr>
        <tr>
          <td align="center" style="padding-top:10px;font-size:0;line-height:0;">
            <span style="display:inline-block;width:34px;height:3px;background:#00aeef;border-radius:999px 0 0 999px;">&nbsp;</span>
            <span style="display:inline-block;width:34px;height:3px;background:#00aeef;">&nbsp;</span>
            <span style="display:inline-block;width:34px;height:3px;background:#623f99;">&nbsp;</span>
            <span style="display:inline-block;width:34px;height:3px;background:#ec008c;">&nbsp;</span>
            <span style="display:inline-block;width:34px;height:3px;background:#ec008c;border-radius:0 999px 999px 0;">&nbsp;</span>
          </td>
        </tr>
      </table>
    </div>
  `.trim();
}

function decodeHtmlEntities(value) {
  const raw = String(value || "");
  if (!raw) return "";

  if (typeof window !== "undefined" && window.document) {
    const textarea = window.document.createElement("textarea");
    textarea.innerHTML = raw;
    return textarea.value;
  }

  return raw
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, "\"")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

function normalizeEmailBodyParagraphs(value, { fallbackParagraphs = ["Write your message here."] } = {}) {
  const normalized = String(value || "").replace(/\r/g, "").trim();
  if (!normalized) return fallbackParagraphs;
  const paragraphs = normalized
    .split(/\n{2,}/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (!paragraphs.length) return fallbackParagraphs;
  return paragraphs;
}

function extractEditableEmailBody(content) {
  const raw = String(content || "").trim();
  if (!raw) return "";
  if (!raw.includes("<")) return raw;

  const startIndex = raw.indexOf(MARKETING_EMAIL_BODY_START_MARKER);
  const endIndex = raw.indexOf(MARKETING_EMAIL_BODY_END_MARKER);
  if (startIndex >= 0 && endIndex > startIndex) {
    const section = raw.slice(startIndex + MARKETING_EMAIL_BODY_START_MARKER.length, endIndex);
    const paragraphMatches = Array.from(section.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi));
    const extracted = paragraphMatches
      .map((match) => decodeHtmlEntities(String(match?.[1] || "").replace(/<[^>]+>/g, " ").trim()))
      .filter(Boolean);
    if (extracted.length) return extracted.join("\n\n");
  }

  const plain = decodeHtmlEntities(
    raw
      .replace(/<style[\s\S]*?<\/style>/gi, "")
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<[^>]+>/g, " ")
      .replace(/\u00a0/g, " ")
      .replace(/\s+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
  return plain || "";
}

function buildBrandedEmailHtml({ reason, title, paragraphs, ctaLabel, ctaUrl }) {
  const paragraphHtml = (paragraphs || [])
    .map(
      (paragraph) =>
        `<p style="margin:0 0 16px;color:#1f2937;font-size:16px;line-height:1.6;">${escapeHtml(paragraph)}</p>`
    )
    .join("");

  const hasCtaUrl = String(ctaUrl || "").trim();
  const hasCtaLabel = String(ctaLabel || "").trim();
  const ctaHtml =
    hasCtaUrl && hasCtaLabel
      ? `<div style="margin:24px 0;">
          <a href="${escapeHtml(ctaUrl)}" style="display:inline-block;background:#623f99;color:#ffffff;text-decoration:none;padding:14px 22px;font-size:15px;font-weight:700;border-radius:4px;">
            ${escapeHtml(ctaLabel)}
          </a>
        </div>`
      : "";

  return `<!doctype html>
<html>
  <body style="margin:0;padding:0;background:#f4f1f8;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f1f8;padding:24px 12px;">
      <tr>
        <td align="center">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:620px;background:#ffffff;border:1px solid #e9e2f0;border-radius:10px;overflow:hidden;">
            <tr>
              <td style="padding:32px 28px 24px;">
                ${buildBrandHeaderHtml()}
                <p style="margin:0 0 8px;color:#00AEEF;font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;">Why you are receiving this</p>
                <p style="margin:0 0 20px;color:#4b5563;font-size:14px;line-height:1.6;">${escapeHtml(reason)}</p>
                <h1 style="margin:0 0 20px;color:#111111;font-size:28px;line-height:1.2;">${escapeHtml(title)}</h1>
                ${MARKETING_EMAIL_BODY_START_MARKER}
                ${paragraphHtml}
                ${MARKETING_EMAIL_BODY_END_MARKER}
                ${ctaHtml}
              </td>
            </tr>
            <tr>
              <td style="padding:20px 28px;border-top:1px solid #efe8f4;background:#faf8fc;">
                <p style="margin:0;color:#6b7280;font-size:12px;line-height:1.6;">This inbox is not monitored.</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
}

function clampThrottlePerMinute(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return MARKETING_DEFAULT_THROTTLE_PER_MINUTE;
  return Math.max(1, Math.min(MARKETING_MAX_THROTTLE_PER_MINUTE, Math.floor(numeric)));
}

function createSectionLoadingState(defaultValue = false) {
  return MARKETING_ALL_SECTIONS.reduce((acc, key) => {
    acc[key] = defaultValue;
    return acc;
  }, {});
}

function toComposerCampaignPayload({ composer, filters, minimumAudience }) {
  const isEmail = String(composer.channel || "").trim().toUpperCase() === "EMAIL";

  const emailContentA = isEmail
    ? buildBrandedEmailHtml({
        reason: DEFAULT_REASON,
        title: composer.titleA || "",
        paragraphs: normalizeEmailBodyParagraphs(composer.contentA, { fallbackParagraphs: [] }),
        ctaLabel: composer.ctaLabel || "",
        ctaUrl: composer.ctaUrl || "",
      })
    : null;

  const emailContentB =
    isEmail && composer.abEnabled
      ? buildBrandedEmailHtml({
          reason: DEFAULT_REASON,
          title: composer.titleB || "",
          paragraphs: normalizeEmailBodyParagraphs(composer.contentB, { fallbackParagraphs: [] }),
          ctaLabel: composer.ctaLabel || "",
          ctaUrl: composer.ctaUrl || "",
        })
      : null;

  return {
    campaignId: composer.campaignId || undefined,
    campaignName: composer.campaignName,
    channel: composer.channel,
    region: filters.region,
    demographics: filters.demographics,
    activityLevel: filters.activityLevel,
    onboardingStage: filters.onboardingStage,
    interests: filters.interests,
    minimumAudience: isEmail ? undefined : minimumAudience,
    scheduleAt: toIsoOrNullFromLocal(composer.scheduleAt),
    timezone: composer.timezone,
    throttlePerMinute: clampThrottlePerMinute(composer.throttlePerMinute),
    abEnabled: Boolean(composer.abEnabled),
    subjectA: composer.subjectA,
    subjectB: composer.abEnabled ? composer.subjectB : null,
    contentA: isEmail ? emailContentA : composer.contentA,
    contentB: composer.abEnabled ? (isEmail ? emailContentB : composer.contentB) : null,
    titleA: composer.titleA || "",
    titleB: composer.abEnabled ? (composer.titleB || "") : null,
    ctaLabel: composer.ctaLabel || "",
    ctaUrl: composer.ctaUrl || "",
  };
}

function encodeMarketingTestRecipientTemplateId(email) {
  return `${MARKETING_TEST_RECIPIENT_PREFIX}${encodeURIComponent(String(email || "").trim())}`;
}

function createDefaultComposer() {
  return { ...DEFAULT_COMPOSER };
}

export default function MarketingWorkspace({ currentUserId }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [minimumAudience] = useState(300);
  const [composer, setComposer] = useState(() => createDefaultComposer());

  const [workspaceData, setWorkspaceData] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(() => createSectionLoadingState(true));
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [schedulingCampaign, setSchedulingCampaign] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [testFeedback, setTestFeedback] = useState("");
  const [testError, setTestError] = useState("");
  const [performanceCampaignId, setPerformanceCampaignId] = useState("");
  const latestRequestRef = useRef(0);
  const didInitialLoadRef = useRef(false);

  const setLoadingForSections = useCallback((sections, isLoading) => {
    const targetSections = Array.isArray(sections) && sections.length
      ? sections
      : MARKETING_ALL_SECTIONS;
    setSectionLoading((prev) => {
      const next = { ...prev };
      targetSections.forEach((section) => {
        if (Object.prototype.hasOwnProperty.call(next, section)) {
          next[section] = isLoading;
        }
      });
      return next;
    });
  }, []);

  const loadWorkspaceData = useCallback(async ({ sections = MARKETING_ALL_SECTIONS } = {}) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoadingForSections(sections, true);
    setError("");

    try {
      const data = await getMarketingWorkspaceData({
        ...filters,
      });
      if (latestRequestRef.current !== requestId) return;
      setWorkspaceData(data || null);
      didInitialLoadRef.current = true;
    } catch (workspaceError) {
      if (latestRequestRef.current !== requestId) return;
      console.error("Failed to load marketing workspace data:", workspaceError);
      setError(workspaceError?.message || "Unable to load marketing data.");
      if (!didInitialLoadRef.current) {
        setWorkspaceData(null);
      }
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoadingForSections(sections, false);
      }
    }
  }, [filters, setLoadingForSections]);

  useEffect(() => {
    const sections = didInitialLoadRef.current
      ? MARKETING_FILTER_DEPENDENT_SECTIONS
      : MARKETING_ALL_SECTIONS;
    loadWorkspaceData({ sections });
  }, [loadWorkspaceData]);

  const kpis = workspaceData?.kpis || EMPTY_ARRAY;
  const retention = workspaceData?.retention || EMPTY_ARRAY;
  const funnelSteps = workspaceData?.funnel || EMPTY_ARRAY;
  const churnTrend = workspaceData?.churnTrend || EMPTY_ARRAY;
  const geoInsights = workspaceData?.geoInsights || EMPTY_ARRAY;
  const campaigns = workspaceData?.campaigns || EMPTY_ARRAY;
  const auditTrail = workspaceData?.auditTrail || EMPTY_ARRAY;

  const estimatedAudience = Number(workspaceData?.estimatedAudience || 0);
  const approvalStatus = String(workspaceData?.approvalStatus || "Unknown");
  const targetingBlocked = Boolean(workspaceData?.targetingBlocked);
  const requiresAdminApproval = Boolean(workspaceData?.requiresAdminApproval);

  const regionOptions = useMemo(
    () => [
      { value: "ALL", label: "All US states" },
      ...US_STATES.map(([code, name]) => ({ value: code, label: name })),
    ],
    []
  );

  const interestOptions = useMemo(() => {
    const dynamic = (workspaceData?.availableInterests || [])
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
    return ["ALL", ...dynamic];
  }, [workspaceData?.availableInterests]);

  const selectedCampaign = useMemo(() => {
    const campaignId = String(composer.campaignId || "").trim();
    if (!campaignId) return campaigns[0] || null;
    return campaigns.find((entry) => String(entry?.campaignId || "").trim() === campaignId) || null;
  }, [campaigns, composer.campaignId]);
  const performanceCampaign = useMemo(() => {
    const normalizedSelected = String(performanceCampaignId || "").trim();
    if (!normalizedSelected) return campaigns[0] || null;
    return campaigns.find((entry) => String(entry?.campaignId || "").trim() === normalizedSelected) || campaigns[0] || null;
  }, [campaigns, performanceCampaignId]);

  const isEmailChannel = String(composer.channel || "").trim().toUpperCase() === "EMAIL";
  const selectedPerformanceIsAb = Boolean(performanceCampaign?.abEnabled);
  const performanceSendAttempted = Number(performanceCampaign?.sendAttempted || 0);
  const performanceSendSent = Number(performanceCampaign?.sendSent || 0);
  const performanceSendFailed = Number(performanceCampaign?.sendFailed || 0);
  const performanceVariantARecipients = Number(
    performanceCampaign?.sendVariantARecipients ||
      (!selectedPerformanceIsAb && performanceSendAttempted > 0 ? performanceSendAttempted : 0)
  );
  const performanceVariantBRecipients = Number(performanceCampaign?.sendVariantBRecipients || 0);
  const performanceDeliverySummary =
    performanceSendAttempted > 0
      ? `${formatCount(performanceSendSent)} / ${formatCount(performanceSendAttempted)}`
      : "Not sent yet";

  const emailPreviewA = useMemo(
    () =>
      buildBrandedEmailHtml({
        reason: DEFAULT_REASON,
        title: composer.titleA || "",
        paragraphs: normalizeEmailBodyParagraphs(composer.contentA),
        ctaLabel: composer.ctaLabel || "",
        ctaUrl: composer.ctaUrl || "",
      }),
    [composer.contentA, composer.titleA, composer.ctaLabel, composer.ctaUrl]
  );

  const emailPreviewB = useMemo(
    () =>
      buildBrandedEmailHtml({
        reason: DEFAULT_REASON,
        title: composer.titleB || "",
        paragraphs: normalizeEmailBodyParagraphs(composer.contentB),
        ctaLabel: composer.ctaLabel || "",
        ctaUrl: composer.ctaUrl || "",
      }),
    [composer.contentB, composer.titleB, composer.ctaLabel, composer.ctaUrl]
  );

  const resetComposerAfterLaunch = useCallback(() => {
    setComposer(createDefaultComposer());
    setTestEmail("");
    setTestError("");
    setTestFeedback("");
  }, []);

  const onSaveDraft = async () => {
    setFeedback("");
    setError("");
    setSavingDraft(true);

    try {
      const response = await saveMarketingCampaign({
        campaign: toComposerCampaignPayload({ composer, filters, minimumAudience }),
        action: "DRAFT",
      });

      const nextCampaignId = String(response?.campaignId || "").trim();
      if (nextCampaignId) {
        setComposer((prev) => ({ ...prev, campaignId: nextCampaignId }));
      }

      setFeedback(`Campaign draft saved${nextCampaignId ? ` (${nextCampaignId})` : ""}.`);
      await loadWorkspaceData({ sections: MARKETING_CAMPAIGN_SECTIONS });
    } catch (saveError) {
      console.error("Failed to save marketing campaign draft:", saveError);
      setError(saveError?.message || "Unable to save campaign draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const onScheduleCampaign = async () => {
    setFeedback("");
    setError("");

    if (!String(composer.scheduleAt || "").trim()) {
      setError("Select a scheduled send time before scheduling a campaign.");
      return;
    }

    setSchedulingCampaign(true);
    try {
      const response = await saveMarketingCampaign({
        campaign: toComposerCampaignPayload({ composer, filters, minimumAudience }),
        action: "SCHEDULE",
      });

      const nextCampaignId = String(response?.campaignId || "").trim();
      if (nextCampaignId) {
        setComposer((prev) => ({ ...prev, campaignId: nextCampaignId }));
      }

      const status = String(response?.status || "SCHEDULED").trim().toUpperCase();
      if (status === "PENDING_APPROVAL") {
        setFeedback("Campaign submitted and is pending admin approval due to large audience size.");
      } else if (status === "SENT") {
        setFeedback("Campaign started and emails were sent.");
        resetComposerAfterLaunch();
      } else if (status === "PARTIAL_SEND") {
        setFeedback("Campaign started with partial delivery. Review campaign performance and audit details.");
        resetComposerAfterLaunch();
      } else if (status === "SCHEDULED") {
        setFeedback("Campaign scheduled successfully.");
        resetComposerAfterLaunch();
      } else {
        setFeedback("Campaign scheduled successfully.");
      }

      await loadWorkspaceData({ sections: MARKETING_CAMPAIGN_SECTIONS });
    } catch (scheduleError) {
      console.error("Failed to schedule marketing campaign:", scheduleError);
      setError(scheduleError?.message || "Unable to schedule campaign.");
    } finally {
      setSchedulingCampaign(false);
    }
  };

  const onSendTestEmail = async () => {
    const email = String(testEmail || "").trim();
    if (!email) {
      setTestFeedback("");
      setTestError("Enter a test email address.");
      return;
    }
    if (!isPlausibleEmail(email)) {
      setTestFeedback("");
      setTestError("Enter a valid test email address.");
      return;
    }

    setError("");
    setFeedback("");
    setTestError("");
    setTestFeedback("");
    setSendingTest(true);
    try {
      await saveMarketingCampaign({
        campaign: {
          ...toComposerCampaignPayload({ composer, filters, minimumAudience }),
          templateId: encodeMarketingTestRecipientTemplateId(email),
        },
        action: "TEST",
      });
      setTestFeedback(`Test email sent to ${email}.`);
    } catch (testError) {
      console.error("Failed to send test email:", testError);
      setTestFeedback("");
      setTestError(testError?.message || "Unable to send test email.");
    } finally {
      setSendingTest(false);
    }
  };

  const loadCampaignIntoComposer = (campaign) => {
    if (!campaign) return;
    const normalizedChannel = String(campaign.channel || "").toUpperCase() === "PUSH" ? "PUSH" : "EMAIL";
    const normalizedContentA =
      normalizedChannel === "EMAIL"
        ? extractEditableEmailBody(campaign.contentA)
        : String(campaign.contentA || "");
    const normalizedContentB =
      normalizedChannel === "EMAIL"
        ? extractEditableEmailBody(campaign.contentB)
        : String(campaign.contentB || "");

    setComposer((prev) => ({
      ...prev,
      campaignId: String(campaign.campaignId || ""),
      channel: normalizedChannel,
      campaignName: String(campaign.campaignName || ""),
      subjectA: String(campaign.subjectA || ""),
      subjectB: String(campaign.subjectB || ""),
      titleA: String(campaign.titleA || ""),
      titleB: String(campaign.titleB || ""),
      contentA: normalizedContentA,
      contentB: normalizedContentB,
      ctaLabel: String(campaign.ctaLabel || ""),
      ctaUrl: String(campaign.ctaUrl || ""),
      scheduleAt: toDateTimeLocalInputValue(campaign.scheduleAt),
      throttlePerMinute: clampThrottlePerMinute(campaign.throttlePerMinute || prev.throttlePerMinute),
      timezone: String(campaign.timezone || prev.timezone),
      abEnabled: Boolean(campaign.abEnabled),
    }));

    setFilters({
      region: String(campaign.region || "ALL").toUpperCase(),
      demographics: String(campaign.demographics || "ALL").toUpperCase(),
      activityLevel: String(campaign.activityLevel || "ALL").toUpperCase(),
      onboardingStage: String(campaign.onboardingStage || "ALL").toUpperCase(),
      interests: String(campaign.interests || "ALL"),
    });

  };

  useEffect(() => {
    if (!campaigns.length) {
      setPerformanceCampaignId("");
      return;
    }
    const hasSelected = campaigns.some(
      (campaign) => String(campaign?.campaignId || "").trim() === String(performanceCampaignId || "").trim()
    );
    if (!hasSelected) {
      setPerformanceCampaignId(String(campaigns[0]?.campaignId || "").trim());
    }
  }, [campaigns, performanceCampaignId]);

  const selectedCampaignMetrics = {
    openRateA: Number(selectedCampaign?.openRateA || 0),
    openRateB: Number(selectedCampaign?.openRateB || 0),
    clickRateA: Number(selectedCampaign?.clickRateA || 0),
    clickRateB: Number(selectedCampaign?.clickRateB || 0),
    conversionRateA: Number(selectedCampaign?.conversionRateA || 0),
    conversionRateB: Number(selectedCampaign?.conversionRateB || 0),
  };
  const maxGeoUsers = useMemo(
    () => geoInsights.reduce((max, entry) => Math.max(max, Number(entry?.users || 0)), 0),
    [geoInsights]
  );
  const isAnySectionLoading = useMemo(
    () => MARKETING_ALL_SECTIONS.some((section) => Boolean(sectionLoading?.[section])),
    [sectionLoading]
  );

  return (
    <section className="opsConsole__section">
      <div className="opsConsole__sectionHead">
        <h2>Marketing</h2>
        <span className="opsConsole__chip">Live</span>
      </div>
      <p className="opsConsole__subtle">
        Updated: {workspaceData?.dataAsOf ? new Date(workspaceData.dataAsOf).toLocaleString() : "Unknown"}
      </p>

      {error ? <div className="opsConsole__error">{error}</div> : null}
      {feedback ? <div className="opsConsole__inlineWarn opsMarketing__safeOk">{feedback}</div> : null}

      <div className="opsMarketing__grid opsMarketing__grid--kpi">
        {sectionLoading.analytics && !kpis.length ? (
          <article className="opsMarketing__card">
            <p className="opsConsole__status">Loading metrics...</p>
          </article>
        ) : (
          kpis.map((card) => (
            <article key={card.key || card.label} className="opsMarketing__card">
              <p className="opsMarketing__label">{card.label}</p>
              <p className="opsMarketing__value">{toKFormatter(card.value)}</p>
            </article>
          ))
        )}
      </div>

      <div className="opsMarketing__grid opsMarketing__grid--double">
        <article className="opsMarketing__card">
          <h3>Retention</h3>
          {sectionLoading.analytics && !retention.length ? (
            <p className="opsConsole__status">Loading retention...</p>
          ) : (
            <div className="opsMarketing__bars">
              {retention.map((point) => (
                <div key={point.label} className="opsMarketing__barRow">
                  <span>{point.label}</span>
                  <div className="opsMarketing__barTrack">
                    <div className="opsMarketing__barFill" style={{ width: `${Math.min(100, point.value || 0)}%` }} />
                  </div>
                  <strong>{formatPct(point.value)}</strong>
                </div>
              ))}
            </div>
          )}
        </article>

        <article className="opsMarketing__card">
          <h3>Funnel</h3>
          {sectionLoading.analytics && !funnelSteps.length ? (
            <p className="opsConsole__status">Loading funnel...</p>
          ) : (
            <div className="opsMarketing__funnel">
              {funnelSteps.map((step) => (
                <div key={step.key || step.label} className="opsMarketing__funnelRow">
                  <div>
                    <p className="opsMarketing__label">{step.label}</p>
                    <p className="opsMarketing__valueSmall">{toKFormatter(step.users)}</p>
                  </div>
                  <p className="opsMarketing__muted">
                    {step.conversionFromPrevious >= 100
                      ? "Baseline"
                      : `Step conversion ${formatPct(step.conversionFromPrevious)}`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </article>
      </div>

      <div className="opsMarketing__grid opsMarketing__grid--double">
        <article className="opsMarketing__card">
          <h3>Churn Trend</h3>
          <p className="opsConsole__subtle">
            Users inactive for 30+ days.
          </p>
          {sectionLoading.analytics && !churnTrend.length ? (
            <p className="opsConsole__status">Loading churn...</p>
          ) : (
            <div className="opsMarketing__trend">
              {churnTrend.map((entry) => (
                <div key={entry.month} className="opsMarketing__trendRow">
                  <span>{entry.month}</span>
                  <div className="opsMarketing__barTrack">
                    <div
                      className="opsMarketing__barFill opsMarketing__barFill--warn"
                      style={{ width: `${Math.min(100, Number(entry.value || 0))}%` }}
                    />
                  </div>
                  <strong>{formatPct(entry.value)}</strong>
                </div>
              ))}
            </div>
          )}
        </article>

        <article className="opsMarketing__card">
          <h3>Audience</h3>
          <div className="opsMarketing__filters">
            <label>
              Region
              <select
                className="opsConsole__filter"
                value={filters.region}
                onChange={(event) => setFilters((prev) => ({ ...prev, region: event.target.value }))}
              >
                {regionOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Demographics
              <select
                className="opsConsole__filter"
                value={filters.demographics}
                onChange={(event) => setFilters((prev) => ({ ...prev, demographics: event.target.value }))}
              >
                {DEMOGRAPHIC_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Audience activity
              <select
                className="opsConsole__filter"
                value={filters.activityLevel}
                onChange={(event) => setFilters((prev) => ({ ...prev, activityLevel: event.target.value }))}
              >
                {ACTIVITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Onboarding stage
              <select
                className="opsConsole__filter"
                value={filters.onboardingStage}
                onChange={(event) => setFilters((prev) => ({ ...prev, onboardingStage: event.target.value }))}
              >
                {ONBOARDING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Interests
              <select
                className="opsConsole__filter"
                value={filters.interests}
                onChange={(event) => setFilters((prev) => ({ ...prev, interests: event.target.value }))}
              >
                {interestOptions.map((interest) => (
                  <option key={interest} value={interest}>{interest === "ALL" ? "All interests" : interest}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="opsMarketing__summary">
            <p><strong>Estimated audience:</strong> {toKFormatter(estimatedAudience)}</p>
            <p><strong>Approval status:</strong> {approvalStatus}</p>
            {sectionLoading.audience ? <p className="opsMarketing__muted">Updating audience...</p> : null}
          </div>
        </article>
      </div>

      <article className="opsMarketing__card">
        <h3>Campaign</h3>
        {sectionLoading.composer ? <p className="opsMarketing__muted">Updating campaign data...</p> : null}
        <div className="opsMarketing__composerGrid">
          <label>
            Campaign name
            <input
              className="opsConsole__search"
              value={composer.campaignName}
              onChange={(event) => setComposer((prev) => ({ ...prev, campaignName: event.target.value }))}
              placeholder="Spring retention push"
            />
          </label>
          <label>
            Channel
            <select
              className="opsConsole__filter"
              value={composer.channel}
              onChange={(event) => setComposer((prev) => ({ ...prev, channel: event.target.value }))}
            >
              <option value="EMAIL">Email</option>
              <option value="PUSH">Push notification</option>
            </select>
          </label>
          <label>
            Timezone
            <select
              className="opsConsole__filter"
              value={composer.timezone}
              onChange={(event) => setComposer((prev) => ({ ...prev, timezone: event.target.value }))}
            >
              {TIMEZONE_OPTIONS.map((timezone) => (
                <option key={timezone} value={timezone}>{timezone}</option>
              ))}
            </select>
          </label>
        </div>

        {isEmailChannel ? (
          <>
            {composer.abEnabled ? (
              <div className="opsMarketing__variantGrid">
                <section className="opsMarketing__variantCard opsMarketing__variantCard--a">
                  <header className="opsMarketing__variantHead">
                    <span className="opsMarketing__variantBadge">A</span>
                    <div>
                      <strong>Variant A</strong>
                      <span>Control</span>
                    </div>
                  </header>
                  <div className="opsMarketing__composerGrid">
                    <label>
                      Email subject
                      <input
                        className="opsConsole__search"
                        value={composer.subjectA}
                        onChange={(event) => setComposer((prev) => ({ ...prev, subjectA: event.target.value }))}
                        placeholder="Variant A subject"
                      />
                    </label>
                    <label>
                      Title
                      <input
                        className="opsConsole__search"
                        value={composer.titleA}
                        onChange={(event) => setComposer((prev) => ({ ...prev, titleA: event.target.value }))}
                        placeholder="Variant A heading"
                      />
                    </label>
                    <label>
                      Body
                      <textarea
                        className="opsConsole__reasonInput"
                        value={composer.contentA}
                        onChange={(event) => setComposer((prev) => ({ ...prev, contentA: event.target.value }))}
                        placeholder="Variant A body copy"
                      />
                    </label>
                  </div>
                </section>

                <section className="opsMarketing__variantCard opsMarketing__variantCard--b">
                  <header className="opsMarketing__variantHead">
                    <span className="opsMarketing__variantBadge">B</span>
                    <div>
                      <strong>Variant B</strong>
                      <span>Test</span>
                    </div>
                  </header>
                  <div className="opsMarketing__composerGrid">
                    <label>
                      Email subject
                      <input
                        className="opsConsole__search"
                        value={composer.subjectB}
                        onChange={(event) => setComposer((prev) => ({ ...prev, subjectB: event.target.value }))}
                        placeholder="Variant B subject"
                      />
                    </label>
                    <label>
                      Title
                      <input
                        className="opsConsole__search"
                        value={composer.titleB}
                        onChange={(event) => setComposer((prev) => ({ ...prev, titleB: event.target.value }))}
                        placeholder="Variant B heading"
                      />
                    </label>
                    <label>
                      Body
                      <textarea
                        className="opsConsole__reasonInput"
                        value={composer.contentB}
                        onChange={(event) => setComposer((prev) => ({ ...prev, contentB: event.target.value }))}
                        placeholder="Variant B body copy"
                      />
                    </label>
                  </div>
                </section>
              </div>
            ) : (
              <div className="opsMarketing__composerGrid">
                <label>
                  Email subject
                  <input
                    className="opsConsole__search"
                    value={composer.subjectA}
                    onChange={(event) => setComposer((prev) => ({ ...prev, subjectA: event.target.value }))}
                    placeholder="Subject line"
                  />
                </label>
                <label>
                  Title
                  <input
                    className="opsConsole__search"
                    value={composer.titleA}
                    onChange={(event) => setComposer((prev) => ({ ...prev, titleA: event.target.value }))}
                    placeholder="Email heading"
                  />
                </label>
                <label>
                  Body
                  <textarea
                    className="opsConsole__reasonInput"
                    value={composer.contentA}
                    onChange={(event) => setComposer((prev) => ({ ...prev, contentA: event.target.value }))}
                    placeholder="Email body copy"
                  />
                </label>
              </div>
            )}

            <div className="opsMarketing__composerGrid opsMarketing__composerGrid--cta">
              <label>
                CTA button label <span className="opsConsole__subtle">(optional)</span>
                <input
                  className="opsConsole__search"
                  value={composer.ctaLabel}
                  onChange={(event) => setComposer((prev) => ({ ...prev, ctaLabel: event.target.value }))}
                  placeholder="Open Better Intentions"
                />
              </label>
              <label>
                CTA link URL <span className="opsConsole__subtle">(optional)</span>
                <input
                  className="opsConsole__search"
                  value={composer.ctaUrl}
                  onChange={(event) => setComposer((prev) => ({ ...prev, ctaUrl: event.target.value }))}
                  placeholder="https://betterintentions.com"
                />
              </label>
            </div>

            <div className={`opsMarketing__emailPreviewGrid ${composer.abEnabled ? "opsMarketing__emailPreviewGrid--ab" : ""}`}>
              <article className="opsMarketing__emailPreviewCard">
                <header className="opsMarketing__emailPreviewHead">
                  <strong>{composer.abEnabled ? "Preview A" : "Preview"}</strong>
                  <span>Subject: {String(composer.subjectA || "No subject yet").trim() || "No subject yet"}</span>
                </header>
                <iframe
                  title="Email preview A"
                  srcDoc={emailPreviewA}
                  className="opsMarketing__emailPreviewFrame"
                  sandbox=""
                />
              </article>
              {composer.abEnabled ? (
                <article className="opsMarketing__emailPreviewCard">
                  <header className="opsMarketing__emailPreviewHead">
                    <strong>Preview B</strong>
                    <span>Subject: {String(composer.subjectB || "No subject yet").trim() || "No subject yet"}</span>
                  </header>
                  <iframe
                    title="Email preview B"
                    srcDoc={emailPreviewB}
                    className="opsMarketing__emailPreviewFrame"
                    sandbox=""
                  />
                </article>
              ) : null}
            </div>

            <div className="opsMarketing__testEmailRow">
              <label>
                Send test email to
                <input
                  type="email"
                  className="opsConsole__search"
                  value={testEmail}
                  onChange={(event) => setTestEmail(event.target.value)}
                  placeholder="you@example.com"
                />
              </label>
              <Button variant="outline" onClick={onSendTestEmail} disabled={sendingTest || !testEmail.trim()}>
                {sendingTest ? "Sending..." : "Send test"}
              </Button>
            </div>
            {testError ? <div className="opsConsole__error">{testError}</div> : null}
            {testFeedback ? <div className="opsConsole__inlineWarn opsMarketing__safeOk">{testFeedback}</div> : null}

          </>
        ) : (
          <>
            {composer.abEnabled ? (
              <div className="opsMarketing__variantGrid">
                <section className="opsMarketing__variantCard opsMarketing__variantCard--a">
                  <header className="opsMarketing__variantHead">
                    <span className="opsMarketing__variantBadge">A</span>
                    <div>
                      <strong>Variant A</strong>
                      <span>Control</span>
                    </div>
                  </header>
                  <div className="opsMarketing__composerGrid">
                    <label>
                      Push title
                      <input
                        className="opsConsole__search"
                        value={composer.subjectA}
                        onChange={(event) => setComposer((prev) => ({ ...prev, subjectA: event.target.value }))}
                        placeholder="Variant A title"
                      />
                    </label>
                    <label>
                      Push body
                      <textarea
                        className="opsConsole__reasonInput"
                        value={composer.contentA}
                        onChange={(event) => setComposer((prev) => ({ ...prev, contentA: event.target.value }))}
                        placeholder="Variant A notification body"
                      />
                    </label>
                  </div>
                </section>

                <section className="opsMarketing__variantCard opsMarketing__variantCard--b">
                  <header className="opsMarketing__variantHead">
                    <span className="opsMarketing__variantBadge">B</span>
                    <div>
                      <strong>Variant B</strong>
                      <span>Test</span>
                    </div>
                  </header>
                  <div className="opsMarketing__composerGrid">
                    <label>
                      Push title
                      <input
                        className="opsConsole__search"
                        value={composer.subjectB}
                        onChange={(event) => setComposer((prev) => ({ ...prev, subjectB: event.target.value }))}
                        placeholder="Variant B title"
                      />
                    </label>
                    <label>
                      Push body
                      <textarea
                        className="opsConsole__reasonInput"
                        value={composer.contentB}
                        onChange={(event) => setComposer((prev) => ({ ...prev, contentB: event.target.value }))}
                        placeholder="Variant B notification body"
                      />
                    </label>
                  </div>
                </section>
              </div>
            ) : (
              <div className="opsMarketing__composerGrid">
                <label>
                  Push title
                  <input
                    className="opsConsole__search"
                    value={composer.subjectA}
                    onChange={(event) => setComposer((prev) => ({ ...prev, subjectA: event.target.value }))}
                    placeholder="Notification title"
                  />
                </label>
                <label>
                  Push body
                  <textarea
                    className="opsConsole__reasonInput"
                    value={composer.contentA}
                    onChange={(event) => setComposer((prev) => ({ ...prev, contentA: event.target.value }))}
                    placeholder="Notification body"
                  />
                </label>
              </div>
            )}

            <div className="opsMarketing__pushPreviewWrap">
              <header className="opsMarketing__pushPreviewHead">
                Push Notification Preview
                <span>Live preview of how notifications will appear</span>
              </header>
              <div className="opsMarketing__pushPreviewGrid">
                <div className="opsMarketing__pushPreview">
                  <div className="opsMarketing__pushPhoneLabel">iOS</div>
                  <div className="opsMarketing__pushPhone opsMarketing__pushPhone--ios">
                    <div className="opsMarketing__pushNotif opsMarketing__pushNotif--ios">
                      <div className="opsMarketing__pushNotifIcon">BI</div>
                      <div className="opsMarketing__pushNotifContent">
                        <div className="opsMarketing__pushNotifApp">BETTER INTENTIONS &middot; now</div>
                        <div className="opsMarketing__pushNotifTitle">
                          {String(composer.subjectA || "").trim() || "Notification title"}
                        </div>
                        <div className="opsMarketing__pushNotifBody">
                          {String(composer.contentA || "").trim() || "Notification body text"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="opsMarketing__pushPreview">
                  <div className="opsMarketing__pushPhoneLabel">Android</div>
                  <div className="opsMarketing__pushPhone opsMarketing__pushPhone--android">
                    <div className="opsMarketing__pushNotif opsMarketing__pushNotif--android">
                      <div className="opsMarketing__pushNotifHeader">
                        <div className="opsMarketing__pushNotifIconSmall">BI</div>
                        <span>Better Intentions</span>
                        <span className="opsMarketing__pushNotifTime">now</span>
                      </div>
                      <div className="opsMarketing__pushNotifTitle">
                        {String(composer.subjectA || "").trim() || "Notification title"}
                      </div>
                      <div className="opsMarketing__pushNotifBody">
                        {String(composer.contentA || "").trim() || "Notification body text"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="opsMarketing__controlsGrid">
          <label>
            Send time
            <input
              type="datetime-local"
              className="opsConsole__filter"
              value={composer.scheduleAt}
              onChange={(event) => setComposer((prev) => ({ ...prev, scheduleAt: event.target.value }))}
            />
          </label>
          <label>
            Throttle (/min)
            <input
              type="number"
              min={1}
              max={MARKETING_MAX_THROTTLE_PER_MINUTE}
              className="opsConsole__filter"
              value={composer.throttlePerMinute}
              onChange={(event) =>
                setComposer((prev) => ({
                  ...prev,
                  throttlePerMinute: clampThrottlePerMinute(event.target.value),
                }))
              }
            />
          </label>
          <div className="opsMarketing__abToggleWrap">
            <span className="opsMarketing__abToggleLabel">A/B testing</span>
            <button
              type="button"
              className={`opsMarketing__abToggle ${composer.abEnabled ? "opsMarketing__abToggle--on" : ""}`}
              aria-pressed={composer.abEnabled}
              onClick={() =>
                setComposer((prev) => ({
                  ...prev,
                  abEnabled: !prev.abEnabled,
                  ...(!prev.abEnabled ? {} : { subjectB: "", contentB: "", titleB: "" }),
                }))
              }
            >
              <span className="opsMarketing__abToggleTrack" aria-hidden="true">
                <span className="opsMarketing__abToggleThumb" />
              </span>
              <span className="opsMarketing__abToggleText">
                {composer.abEnabled ? "Enabled" : "Disabled"}
              </span>
            </button>
          </div>
        </div>

        {composer.abEnabled ? (
          <div className="opsMarketing__abTableWrap">
            <table className="opsConsole__table">
              <thead>
                <tr>
                  <th>Variant</th>
                  <th>Open rate</th>
                  <th>Click rate</th>
                  <th>Conversion rate</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>A</td>
                  <td>{formatPct(selectedCampaignMetrics.openRateA)}</td>
                  <td>{formatPct(selectedCampaignMetrics.clickRateA)}</td>
                  <td>{formatPct(selectedCampaignMetrics.conversionRateA)}</td>
                </tr>
                <tr>
                  <td>B</td>
                  <td>{formatPct(selectedCampaignMetrics.openRateB)}</td>
                  <td>{formatPct(selectedCampaignMetrics.clickRateB)}</td>
                  <td>{formatPct(selectedCampaignMetrics.conversionRateB)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="opsMarketing__actions">
          <Button
            variant="outline"
            onClick={() => loadWorkspaceData({ sections: MARKETING_ALL_SECTIONS })}
            disabled={isAnySectionLoading}
          >
            Refresh
          </Button>
          <Button variant="outline" onClick={onSaveDraft} disabled={savingDraft}>
            {savingDraft ? "Saving..." : "Save draft"}
          </Button>
          <Button variant="primary" onClick={onScheduleCampaign} disabled={targetingBlocked || schedulingCampaign}>
            {schedulingCampaign ? "Scheduling..." : "Schedule"}
          </Button>
        </div>
      </article>

      <div className="opsMarketing__grid opsMarketing__grid--double">
        <article className="opsMarketing__card">
          <h3>Regional Insights</h3>
          <p className="opsConsole__subtle">All regions, sorted by users.</p>
          {sectionLoading.regional && !geoInsights.length ? (
            <p className="opsConsole__status">Loading regional insights...</p>
          ) : (
            <div className="opsMarketing__regionTableWrap">
              <table className="opsMarketing__regionTable">
                <thead>
                  <tr>
                    <th>Region</th>
                    <th>Users</th>
                    <th>Interest</th>
                    <th>Response</th>
                  </tr>
                </thead>
                <tbody>
                  {geoInsights.map((entry) => {
                    const users = Number(entry?.users || 0);
                    const width = maxGeoUsers > 0 ? Math.max(6, (users / maxGeoUsers) * 100) : 0;
                    return (
                      <tr key={entry.region}>
                        <td className="opsMarketing__regionName">{entry.region}</td>
                        <td>
                          <div className="opsMarketing__regionUsers">
                            <span>{toKFormatter(users)}</span>
                            <div className="opsMarketing__regionBarTrack">
                              <div className="opsMarketing__regionBarFill" style={{ width: `${width}%` }} />
                            </div>
                          </div>
                        </td>
                        <td>{formatPct(entry.interest)}</td>
                        <td>{formatPct(entry.responseRate)}</td>
                      </tr>
                    );
                  })}
                  {!geoInsights.length ? (
                    <tr>
                      <td colSpan={4} className="opsConsole__status">No regional data available yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </article>
      </div>

      <article className="opsMarketing__card">
        <h3>Campaign Performance</h3>
        <p className="opsConsole__subtle">Select a campaign to see open rate, click rate, and conversion performance.</p>
        {sectionLoading.history ? <p className="opsMarketing__muted">Updating campaigns...</p> : null}
        <div className="opsMarketing__campaignPerf">
          {campaigns.length ? (
            <>
              <aside className="opsMarketing__campaignList" aria-label="Campaign list">
                <p className="opsMarketing__campaignListTitle">Saved campaigns</p>
                {campaigns.map((campaign, index) => {
                  const campaignId = String(campaign?.campaignId || "").trim();
                  const isActive = campaignId && campaignId === String(performanceCampaign?.campaignId || "").trim();
                  const sendAttempted = Number(campaign?.sendAttempted || 0);
                  const sendSent = Number(campaign?.sendSent || 0);
                  const sendFailed = Number(campaign?.sendFailed || 0);
                  return (
                    <button
                      key={campaignId || `campaign-${index}`}
                      type="button"
                      className={`opsMarketing__campaignListItem ${isActive ? "opsMarketing__campaignListItem--active" : ""}`}
                      onClick={() => setPerformanceCampaignId(campaignId)}
                    >
                      <div className="opsMarketing__campaignListTop">
                        <strong>{campaign.campaignName || campaignId || "Untitled campaign"}</strong>
                        <span className="opsConsole__chip">{campaign.status || "DRAFT"}</span>
                      </div>
                      <p>{String(campaign.channel || "EMAIL").toUpperCase()} · {toKFormatter(campaign.estimatedAudience)} audience</p>
                      <p>
                        Delivery: {sendAttempted > 0 ? `${formatCount(sendSent)} / ${formatCount(sendAttempted)} sent` : "Not sent yet"}
                        {sendAttempted > 0 ? ` · ${formatCount(sendFailed)} failed` : ""}
                      </p>
                      <p>{campaign.updatedAt ? new Date(campaign.updatedAt).toLocaleString() : "No updates yet"}</p>
                    </button>
                  );
                })}
              </aside>

              <section className="opsMarketing__campaignDetail">
                <div className="opsMarketing__campaignDetailHead">
                  <div>
                    <h4>{performanceCampaign?.campaignName || performanceCampaign?.campaignId || "Campaign details"}</h4>
                    <p>
                      {(performanceCampaign?.channel || "EMAIL").toUpperCase()} ·
                      {" "}
                      {toKFormatter(performanceCampaign?.estimatedAudience || 0)}
                      {" "}
                      audience ·
                      {" "}
                      {performanceCampaign?.status || "DRAFT"}
                    </p>
                  </div>
                  {performanceCampaign ? (
                    <Button variant="outline" onClick={() => loadCampaignIntoComposer(performanceCampaign)}>
                      Open in composer
                    </Button>
                  ) : null}
                </div>

                <div className="opsMarketing__campaignSummaryGrid">
                  <article className="opsMarketing__campaignSummaryCard">
                    <p>Status</p>
                    <strong>{performanceCampaign?.status || "DRAFT"}</strong>
                  </article>
                  <article className="opsMarketing__campaignSummaryCard">
                    <p>Audience</p>
                    <strong>{toKFormatter(performanceCampaign?.estimatedAudience || 0)}</strong>
                  </article>
                  <article className="opsMarketing__campaignSummaryCard">
                    <p>Sent / Attempted</p>
                    <strong>{performanceDeliverySummary}</strong>
                  </article>
                  <article className="opsMarketing__campaignSummaryCard">
                    <p>Failed</p>
                    <strong>{formatCount(performanceSendFailed)}</strong>
                  </article>
                  <article className="opsMarketing__campaignSummaryCard">
                    <p>Last send</p>
                    <strong>
                      {performanceCampaign?.sendLastAttemptAt
                        ? new Date(performanceCampaign.sendLastAttemptAt).toLocaleString()
                        : "No sends yet"}
                    </strong>
                  </article>
                  <article className="opsMarketing__campaignSummaryCard">
                    <p>Updated</p>
                    <strong>{performanceCampaign?.updatedAt ? new Date(performanceCampaign.updatedAt).toLocaleString() : "No updates yet"}</strong>
                  </article>
                  <article className="opsMarketing__campaignSummaryCard">
                    <p>Experiment</p>
                    <strong>{selectedPerformanceIsAb ? "A/B enabled" : "Single variant"}</strong>
                  </article>
                </div>
                {performanceCampaign?.sendFailureDetail ? (
                  <p className="opsMarketing__muted">Delivery detail: {performanceCampaign.sendFailureDetail}</p>
                ) : null}

                <div className="opsMarketing__variantBreakdown">
                  <article className="opsMarketing__variantPerfCard opsMarketing__variantPerfCard--a">
                    <h5>{selectedPerformanceIsAb ? "Variant A (Control)" : "Primary Variant"}</h5>
                    <div className="opsMarketing__variantStatList">
                      <div className="opsMarketing__variantStatRow">
                        <span>Open rate</span>
                        <strong>{formatPct(performanceCampaign?.openRateA)}</strong>
                      </div>
                      <div className="opsMarketing__variantStatRow">
                        <span>Click rate</span>
                        <strong>{formatPct(performanceCampaign?.clickRateA)}</strong>
                      </div>
                      <div className="opsMarketing__variantStatRow">
                        <span>Conversion rate</span>
                        <strong>{formatPct(performanceCampaign?.conversionRateA)}</strong>
                      </div>
                      <div className="opsMarketing__variantStatRow">
                        <span>Recipients</span>
                        <strong>{formatCount(performanceVariantARecipients)}</strong>
                      </div>
                    </div>
                  </article>

                  {selectedPerformanceIsAb ? (
                    <article className="opsMarketing__variantPerfCard opsMarketing__variantPerfCard--b">
                      <h5>Variant B (Test)</h5>
                      <div className="opsMarketing__variantStatList">
                        <div className="opsMarketing__variantStatRow">
                          <span>Open rate</span>
                          <strong>{formatPct(performanceCampaign?.openRateB)}</strong>
                        </div>
                        <div className="opsMarketing__variantStatRow">
                          <span>Click rate</span>
                          <strong>{formatPct(performanceCampaign?.clickRateB)}</strong>
                        </div>
                        <div className="opsMarketing__variantStatRow">
                          <span>Conversion rate</span>
                          <strong>{formatPct(performanceCampaign?.conversionRateB)}</strong>
                        </div>
                        <div className="opsMarketing__variantStatRow">
                          <span>Recipients</span>
                          <strong>{formatCount(performanceVariantBRecipients)}</strong>
                        </div>
                      </div>
                    </article>
                  ) : null}
                </div>
              </section>
            </>
          ) : (
            <p className="opsConsole__status">
              {sectionLoading.history ? "Loading campaigns..." : "No campaigns saved yet."}
            </p>
          )}
        </div>
      </article>

      <article className="opsMarketing__card">
        <h3>Audit & Approval</h3>
        <p className="opsConsole__subtle">
          Actions are audited. Large sends may require Admin approval.
        </p>
        <p className="opsMarketing__summaryLine">
          <strong>Large send policy:</strong> {requiresAdminApproval ? "Approval currently required" : "No approval required"}
        </p>
        <p className="opsConsole__subtle"><strong>Current actor:</strong> {currentUserId || "Unknown"}</p>
        {sectionLoading.governance ? <p className="opsMarketing__muted">Updating audit trail...</p> : null}

        <div className="opsMarketing__abTableWrap">
          <table className="opsConsole__table">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {auditTrail.map((entry) => (
                <tr key={entry.auditId}>
                  <td>{entry.actedAt ? new Date(entry.actedAt).toLocaleString() : "—"}</td>
                  <td>{entry.actorUserName || entry.actorUserId || "Unknown"}</td>
                  <td>{entry.actionType}</td>
                  <td>{entry.detail || ""}</td>
                </tr>
              ))}
              {!auditTrail.length ? (
                <tr>
                  <td colSpan={4} className="opsConsole__status">
                    {sectionLoading.governance ? "Loading audit activity..." : "No marketer actions recorded yet."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </article>
    </section>
  );
}
